import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Gem, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "../components/Reveal";
import AssessmentForm from "../components/AssessmentForm";
import { IMAGES, SERVICE_AREAS } from "../data/content";
import Seo from "../components/Seo";
import { responsive } from "../lib/img";

const DOMAIN = "https://www.speedyresurfacing.com.au";

const TRUST = [
  { icon: ShieldCheck, t: "CM3 certified", d: "Licensed, insured and safety-documented for schools, clubs and councils." },
  { icon: Clock, t: "Round-the-clock", d: "We work evenings, weekends and holiday windows to suit your site." },
  { icon: Gem, t: "Premium materials", d: "Professional-grade coatings and Australian-standard court paints only." },
];

export default function Assessment() {
  return (
    <div className="min-h-screen noise" data-testid="assessment-page">
      <Seo
        title="Free Court Site Assessment Sydney | Speedy Resurfacing"
        description="Request a free, obligation-free court resurfacing assessment in Sydney & NSW. Just your full name, mobile number and email address — we come back with an itemised proposal."
        path="/assessment"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: "Court resurfacing site assessment",
              serviceType: "Court resurfacing and line marking assessment",
              description: "Free on-site court assessment and itemised, obligation-free proposal across Sydney and NSW.",
              provider: { "@type": "LocalBusiness", name: "Speedy Resurfacing", telephone: "0466 495 495", url: DOMAIN },
              areaServed: SERVICE_AREAS,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: DOMAIN + "/" },
                { "@type": "ListItem", position: 2, name: "Site Assessment", item: DOMAIN + "/assessment" },
              ],
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-14">
        <img {...responsive(IMAGES.tanTeal, "100vw", [640, 1000, 1600])} decoding="async" alt="Court resurfacing site assessment Sydney" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-obsidian/50" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <nav className="flex items-center gap-2 font-mono text-xs text-zinc-500 mb-6">
            <Link to="/" className="hover:text-neon">Home</Link><span>/</span>
            <span className="text-zinc-300">Site Assessment</span>
          </nav>
          <Overline>Obligation-free · Sydney &amp; NSW</Overline>
          <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">
            SECURE YOUR CUSTOM <span className="gold-text">TRANSFORMATION.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-300">
            Your full name, mobile number and email address — that's all we need. Our specialists call you to arrange
            a site visit and come back with a comprehensive, itemised proposal.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-5 md:px-10 pb-24">
        <Reveal>
          <AssessmentForm testid="assessment-form" />
        </Reveal>

        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {TRUST.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.06}>
              <div className="h-full rounded-lg border border-white/10 bg-obsidian-800 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-sm bg-neon/10 text-neon"><c.icon size={18} /></span>
                <h3 className="mt-4 font-heading font-bold text-lg">{c.t}</h3>
                <p className="mt-1.5 text-sm text-zinc-400">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-12">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Where we work</span>
            <div className="mt-4 flex flex-wrap gap-2">
              {SERVICE_AREAS.map((a) => (
                <span key={a} className="rounded-sm border border-white/10 px-3 py-1.5 text-sm text-zinc-400">{a}</span>
              ))}
            </div>
            <Link to="/blog-articles/beyond-the-baseline" className="group mt-8 inline-flex items-center gap-2 font-bold text-gold hover:text-gold-hover">
              Read: Beyond the Baseline — court design &amp; resurfacing
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
